import {
  BalanceData, Fix64, FeedPlan, FusionPlan, FusionRequest, GearItem, GearTarget, PlayerProfile, Rarity, RaisePreview, SalvageYield,
  fuse, feed, fusionFodder, itemPower, kindOf, mythicEffectsFor, planFeed, planFusion, primaryValue, raiseCap, raiseGearTo, raisePreview,
  salvageYield, sellBlocker, sellPrice, sellRefund,
} from "@highmarch/domain";
import { outclassed, OutclassedOptions } from "./gearView";

/**
 * Read models and actions for the gear v2 forge (docs/GEAR_V2.md §API): the item card (level, cap, cost, the next
 * level's stats, what salvage and a sale pay), the raise preview, fusion candidates and preview, and the feed /
 * clear-junk previews. Pure over the profile; the actions are the domain's own, so a preview is what the action does.
 */

/** Everything the item card shows about one item. */
export interface ItemCardView {
  readonly item: GearItem;
  readonly level: number;
  /** The level it can be raised to now (its wearer's level; unworn, the best owner of its kind). */
  readonly cap: number;
  /** Gear score (§7) now. */
  readonly power: number;
  /** Primary now (fraction). */
  readonly primary: Fix64;
  /** The forge row: the next level's cost and stats, how far the essence held goes. */
  readonly raise: RaisePreview;
  /** Why "Raise" is greyed (null: it can raise now). */
  readonly raiseBlocked: "at cap" | "not enough essence" | null;
  /** Forge Essence held. */
  readonly essence: number;
  /** What salvage pays. */
  readonly salvage: SalvageYield;
  /** What the Trading Post pays (Marks, and the essence refund), or why it will not take it. */
  readonly sell: { readonly marks: number; readonly essence: number } | { readonly blocked: string };
  /** Fusion: fodder items owned for it and the sigils held of its rarity; null for a Mythic (it never fuses). */
  readonly fusion: { readonly fodder: number; readonly sigils: number; readonly ready: boolean } | null;
}

export function itemCard(profile: PlayerProfile, item: GearItem): ItemCardView {
  const B = profile.balance;
  const raise = raisePreview(profile, item);
  const blocked = sellBlocker(B, item);
  const fodder = item.rarity >= Rarity.Mythic ? null : fusionFodder(profile, item).length;
  const sigils = item.rarity >= Rarity.Mythic ? 0 : profile.forge.sigils[item.rarity];
  return {
    item, level: item.level, cap: raise.cap, power: itemPower(B, item), primary: primaryValue(B, item), raise,
    raiseBlocked: item.level >= raise.cap ? "at cap" : profile.forge.essence < raise.nextCost ? "not enough essence" : null,
    essence: profile.forge.essence, salvage: salvageYield(B, item),
    sell: blocked !== null ? { blocked } : { marks: sellPrice(B, item), essence: sellRefund(B, item) },
    fusion: fodder === null ? null : { fodder, sigils, ready: fodder + Math.min(sigils, B.gear.fusionMaxSigils) >= 2 },
  };
}

/** Raises an item toward `toLevel` (default: one level) as far as essence and its cap allow. */
export function raiseItem(profile: PlayerProfile, itemId: number, toLevel?: number): { readonly levels: number; readonly essence: number } | string {
  const it = profile.findGear(itemId);
  if (!it) return "no such item";
  return raiseGearTo(profile, itemId, toLevel ?? it.level + 1);
}

/** Raises to the cap, or as far as the essence goes ("Max"). */
export function raiseItemMax(profile: PlayerProfile, itemId: number): { readonly levels: number; readonly essence: number } | string {
  const it = profile.findGear(itemId);
  if (!it) return "no such item";
  return raiseGearTo(profile, itemId, raiseCap(profile, it));
}

// ------------------------------------------------------------------ fusion

/** A Mythic effect the player may forge into a Legendary → Mythic fusion. */
export interface MythicChoice { readonly index: number; readonly id: string; readonly name: string; readonly towerId: string; readonly description: string }

/** The fusion sheet for a keeper: the fodder it can take, sigils, the suggested pick and (to Mythic) the effect choices. */
export interface FusionCandidates {
  readonly keeper: GearItem;
  /** Fodder in the order a player would least miss (lowest gear power, lowest level, oldest). */
  readonly fodder: readonly GearItem[];
  readonly sigilsHeld: number;
  readonly maxSigils: number;
  /** The suggested fusion: the two least-missed fodder items, sigils for what is short; null when it cannot make three. */
  readonly suggested: FusionRequest | null;
  /** Legendary → Mythic: the effects that fit the keeper's kind (empty otherwise). */
  readonly mythicChoices: readonly MythicChoice[];
  /** The fusion's Forge Essence fee, and whether the essence held (with the suggested fodder's own essence back) covers it. */
  readonly fee: number;
  readonly affordable: boolean;
}

export function fusionCandidates(profile: PlayerProfile, keeper: GearItem): FusionCandidates {
  const B = profile.balance;
  const fodder = fusionFodder(profile, keeper);
  const sigilsHeld = keeper.rarity >= Rarity.Mythic ? 0 : profile.forge.sigils[keeper.rarity];
  const real = fodder.slice(0, 2).map((f) => f.id);
  const sigils = Math.min(2 - real.length, sigilsHeld, B.gear.fusionMaxSigils);
  const suggested = keeper.rarity < Rarity.Mythic && real.length + sigils === 2 ? { keeper: keeper.id, fodder: real, sigils } : null;
  const mythicChoices = keeper.rarity === Rarity.Legendary
    ? mythicEffectsFor(B, kindOf(B, keeper)).map((i) => { const m = B.gear.mythicEffects[i]; return { index: i, id: m.id, name: m.name, towerId: m.towerId, description: m.description }; })
    : [];
  const fee = keeper.rarity >= Rarity.Mythic ? 0 : B.gear.fusionEssence[keeper.rarity + 1];
  const back = fodder.slice(0, 2).reduce((n, f) => n + f.essenceSpent, 0);
  return { keeper, fodder, sigilsHeld, maxSigils: B.gear.fusionMaxSigils, suggested, mythicChoices, fee, affordable: profile.forge.essence + back >= fee };
}

/** A fusion as the sheet previews it: the plan (the result item) and the keeper's power and primary before and after. */
export interface FusionPreview {
  readonly plan: FusionPlan;
  readonly powerBefore: number;
  readonly powerAfter: number;
  readonly primaryBefore: Fix64;
  readonly primaryAfter: Fix64;
}

export function fusionPreview(profile: PlayerProfile, req: FusionRequest): FusionPreview | string {
  const plan = planFusion(profile, req);
  if (typeof plan === "string") return plan;
  const B = profile.balance;
  return { plan, powerBefore: itemPower(B, plan.keeper), powerAfter: itemPower(B, plan.result), primaryBefore: primaryValue(B, plan.keeper), primaryAfter: primaryValue(B, plan.result) };
}

/** Fuses (the domain rule). */
export function fuseItems(profile: PlayerProfile, req: FusionRequest): FusionPlan | string { return fuse(profile, req); }

/** Groups the stash could fuse now: per rarity and kind, how many unworn, unlocked items (and sigils) there are. */
export interface FusableGroup { readonly rarity: Rarity; readonly kind: GearTarget; readonly items: number; readonly sigils: number }

/** Every rarity × kind with at least three (items + usable sigils, one real item at least): the Armoury's FUSE badge. */
export function fusableGroups(profile: PlayerProfile): FusableGroup[] {
  const B = profile.balance;
  const out: FusableGroup[] = [];
  for (let r = Rarity.Common; r < Rarity.Mythic; r++) {
    for (const kind of [GearTarget.Tower, GearTarget.Base]) {
      const all = profile.gear.filter((g) => g.rarity === r && kindOf(B, g) === kind);
      const free = all.filter((g) => g.equippedTo === null && !g.locked).length;
      const sigils = Math.min(profile.forge.sigils[r], B.gear.fusionMaxSigils);
      // The keeper may be worn or locked; the two others must be free items or sigils.
      if (all.length >= 1 && Math.min(free, all.length - 1) + sigils >= 2) out.push({ rarity: r as Rarity, kind, items: all.length, sigils: profile.forge.sigils[r] });
    }
  }
  return out;
}

// ------------------------------------------------------------------ feeding and clearing junk

export interface FeedOptions extends OutclassedOptions {
  /** Allow Epic and Legendary fodder (an explicit confirmation in the UI). */
  readonly confirmEpic?: boolean;
}

/**
 * The items a "Feed" into `target` takes by default: unworn, unlocked, not NEW, below Epic (unless confirmed), never
 * Mythic, never the target — the outclassed (junk) items first, then the rest weakest first.
 */
export function feedCandidates(profile: PlayerProfile, target: GearItem, opts: FeedOptions = {}): GearItem[] {
  const B = profile.balance;
  const junk = new Set(outclassed(profile, opts).map((g) => g.id));
  const ceiling = opts.confirmEpic ? Rarity.Legendary : (B.gear.setMinRarity - 1) as Rarity;
  return profile.gear
    .filter((g) => g.id !== target.id && g.equippedTo === null && !g.locked && g.rarity <= ceiling && (opts.seenUpTo === undefined || g.id <= opts.seenUpTo))
    .sort((a, b) => Number(junk.has(b.id)) - Number(junk.has(a.id)) || itemPower(B, a) - itemPower(B, b) || a.id - b.id);
}

/** A feed preview: the plan, or why not. */
export function feedPreview(profile: PlayerProfile, targetId: number, fodderIds: readonly number[], confirmEpic = false): FeedPlan | string {
  return planFeed(profile, targetId, fodderIds, confirmEpic);
}

/** Feeds (the domain rule). */
export function feedItems(profile: PlayerProfile, targetId: number, fodderIds: readonly number[], confirmEpic = false): FeedPlan | string {
  return feed(profile, targetId, fodderIds, confirmEpic);
}

/** What "Clear junk" would take and pay: the outclassed items (never worn, locked, NEW, Epic+ or Mythic). */
export function clearJunkPreview(profile: PlayerProfile, opts: OutclassedOptions = {}): { readonly items: readonly GearItem[]; readonly gold: number; readonly essence: number } {
  const items = outclassed(profile, opts);
  const ys = items.map((it) => salvageYield(profile.balance, it));
  return { items, gold: ys.reduce((a, y) => a + y.gold, 0), essence: ys.reduce((a, y) => a + y.essence, 0) };
}

/** The essence a whole stash of spare items would give (the Forge header's "spare: ~n essence"). */
export function spareEssence(balance: BalanceData, items: readonly GearItem[]): number {
  return items.reduce((a, it) => a + salvageYield(balance, it).essence, 0);
}
